import { useTranslation } from '@/i18n'
import {
  EventhouseProvisionStatus,
  type EventhouseProvisionStatusType,
} from '@/types/connector'

type EventhouseProvisionTagProps = {
  status: EventhouseProvisionStatusType
  /** provision_error detail from the backend. */
  error?: string
}

const baseStyles = 'w-fit inline rounded-lg py-1 px-3 text-center text-sm font-medium'

// queued/provisioning render identically ("Provisioning…"): the split is a
// backend state-machine detail, not a user-meaningful difference.
const statusClasses: Record<EventhouseProvisionStatusType, string> = {
  [EventhouseProvisionStatus.QUEUED]:
    'text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
  [EventhouseProvisionStatus.PROVISIONING]:
    'text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
  [EventhouseProvisionStatus.SEEDED]:
    'text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
  [EventhouseProvisionStatus.LINKED]:
    'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
  [EventhouseProvisionStatus.ERROR]:
    'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30',
}

const statusKey: Record<EventhouseProvisionStatusType, string> = {
  [EventhouseProvisionStatus.QUEUED]: 'provision.provisioning',
  [EventhouseProvisionStatus.PROVISIONING]: 'provision.provisioning',
  [EventhouseProvisionStatus.SEEDED]: 'provision.linking',
  [EventhouseProvisionStatus.LINKED]: 'provision.live',
  [EventhouseProvisionStatus.ERROR]: 'provision.error',
}

/**
 * Provisioning badge for eventhouse connectors. These rows never heartbeat,
 * so the standard online/disconnected tag (active && last_connection < 60s)
 * would decay to "Disconnected" a minute after linking — this renders the
 * config state machine instead.
 */
const EventhouseProvisionTag = ({ status, error }: EventhouseProvisionTagProps) => {
  const { t } = useTranslation()
  const failed = status === EventhouseProvisionStatus.ERROR
  return (
    <div className="flex flex-col gap-1 items-start">
      <span
        className={`${baseStyles} ${statusClasses[status]}`}
        title={failed ? error : undefined}
      >
        {t(`connectors:${statusKey[status]}`)}
      </span>
      {/* The reason is rendered, not only hovered. A tooltip-only failure reads
          as "it just didn't work": the actual cause here was a Fabric 403
          naming the exact missing setting, and it was invisible until you
          happened to hover the badge. `title` is kept for the full untruncated
          text. */}
      {failed && error && (
        <span
          className="max-w-[28rem] text-xs text-red-700 dark:text-red-400 line-clamp-3 break-words"
          title={error}
        >
          {error}
        </span>
      )}
    </div>
  )
}

export default EventhouseProvisionTag
